import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, Chip, OutlinedInput,
  CircularProgress, Alert, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, FormControlLabel, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ExpandMore as ExpandMoreIcon, History as HistoryIcon, UploadFile as UploadFileIcon, InsertDriveFile as FileIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { contentService, dropdownService } from '../services/api';
import api from '../services/api';
import axios from 'axios';
import ContentRoadmap from '../components/ContentRoadmap';
import { useAuth } from '../context/AuthContext';
import IconButton from '@mui/material/IconButton';

import { openScriptSecure } from '../utils/fileUrl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

function ContentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { user } = useAuth();

  const canUploadScript = ['owner', 'manager', 'lead', 'director'].some(r =>
    user?.role === r || (user?.roles || []).includes(r)
  );

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [dropdowns, setDropdowns] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [validationErrors, setValidationErrors] = useState([]);

  // Script file state
  const [scriptFile, setScriptFile] = useState(null); // existing file metadata from DB
  const [pendingScriptFile, setPendingScriptFile] = useState(null); // file selected before save (new content)
  const [newEffortRow, setNewEffortRow] = useState({ editorId: '', editorName: '', workType: '', effort: '' });
  const [newDirectorEffortRow, setNewDirectorEffortRow] = useState({ directorId: '', directorName: '', workType: '', effort: '' });
  const [scriptUploading, setScriptUploading] = useState(false);
  const [scriptDeleting, setScriptDeleting] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false);
  const originalStatusRef = React.useRef(null);
  const originalContentIdRef = React.useRef(null);

  const [formData, setFormData] = useState({
    contentId: '',
    contentTitle: '',
    channel: '',
    channelId: '',
    contentType: '',
    contentTypeId: '',
    genre: '',
    castingList: [],
    castingIds: [],
    locationDetails: '',
    shootDate: '',
    director: '',
    directorId: '',
    editor: '',
    editorId: '',
    cinematographer: '',
    cinematographerId: '',
    writer: '',
    writerId: '',
    status: 'Content Ready',
    releaseDate: '',
    videoPerformance: '',
    remarks: '',
    editorEfforts: [],
    directorEfforts: [],
    repositoryUploadStatus: 'Not Started',
    ytVideoUrl: '',
    facebookUrl: '',
    integratedAdIncluded: false
  });

  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const loadHistory = async () => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      const res = await contentService.getAuditLogs(id);
      setHistoryLogs(res.data.logs || []);
    } catch (e) {
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryExpand = (_, expanded) => {
    setHistoryExpanded(expanded);
    if (expanded && historyLogs.length === 0) loadHistory();
  };

  useEffect(() => {
    fetchDropdowns();
    if (isEdit) {
      fetchContent();
    } else {
      // Reset form when switching to "Add New" mode
      setFormData({
        contentId: '',
        contentTitle: '',
        channel: '',
        channelId: '',
        contentType: '',
        contentTypeId: '',
        castingList: [],
        castingIds: [],
        locationDetails: '',
        shootDate: '',
        director: '',
        directorId: '',
        editor: '',
        editorId: '',
        cinematographer: '',
        cinematographerId: '',
        writer: '',
        writerId: '',
        status: 'Content Ready',
        releaseDate: '',
        videoPerformance: '',
        remarks: '',
        repositoryUploadStatus: 'Not Started',
        ytVideoUrl: '',
        facebookUrl: ''
      });
    }
  }, [id, isEdit]);

  // Refetch dropdowns when component becomes visible (e.g., after navigating from Settings)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDropdowns();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', fetchDropdowns);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', fetchDropdowns);
    };
  }, []);

  const fetchDropdowns = async () => {
    try {
      const response = await dropdownService.getAll();
      setDropdowns(response.data.data);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await contentService.getById(id);
      const content = response.data.data.content;
      
      // Ensure arrays are properly parsed
      let castingList = content.castingList || [];
      let castingIds = content.castingIds || [];
      
      // Parse if they're strings (from database)
      if (typeof castingList === 'string') {
        try {
          castingList = JSON.parse(castingList);
        } catch (e) {
          castingList = [];
        }
      }
      
      if (typeof castingIds === 'string') {
        try {
          castingIds = JSON.parse(castingIds);
        } catch (e) {
          castingIds = [];
        }
      }
      
      setFormData({
        ...content,
        shootDate: content.shootDate || '',
        releaseDate: content.releaseDate || '',
        castingList: Array.isArray(castingList) ? castingList : [],
        castingIds: Array.isArray(castingIds) ? castingIds : [],
        editorEfforts: (() => { try { const v = content.editorEfforts; return Array.isArray(v) ? v : (v ? JSON.parse(v) : []); } catch (_) { return []; } })(),
        directorEfforts: (() => { try { const v = content.directorEfforts; return Array.isArray(v) ? v : (v ? JSON.parse(v) : []); } catch (_) { return []; } })(),
        directorId: content.directorId || '',
        editorId: content.editorId || '',
        cinematographerId: content.cinematographerId || '',
        writerId: content.writerId || ''
      });
      // Load existing script file
      if (content.scriptFile) {
        try {
          setScriptFile(typeof content.scriptFile === 'string'
            ? JSON.parse(content.scriptFile)
            : content.scriptFile);
        } catch (_) {}
      }
      originalStatusRef.current = content.status;
      originalContentIdRef.current = content.contentId || null;
    } catch (error) {
      console.error('Error fetching content:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Generate Content ID from channel and content type
  const generateContentId = async (channel, contentType, channelIds, contentTypeIds) => {
    if (!channel || !contentType) return '';
    
    const channelCode = channelIds?.[channel] || 'XXX';
    const typeCode = contentTypeIds?.[contentType] || 'XX';
    
    // Format: DDMMYY
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const dateCode = `${day}${month}${year}`;
    
    try {
      // Get sequential number from backend
      const response = await axios.get(`${API_URL}/content/next-sequence/${channelCode}/${typeCode}/${dateCode}`);
      const sequenceNum = String(response.data.data.nextSequence).padStart(3, '0');
      
      return `${channelCode}-${typeCode}-${dateCode}-${sequenceNum}`;
    } catch (error) {
      console.error('Error generating content ID:', error);
      // Fallback to timestamp if API fails
      const timestamp = Date.now().toString().slice(-6);
      return `${channelCode}-${typeCode}-${dateCode}-${timestamp}`;
    }
  };

  // In edit mode, rebuild the content ID keeping the original date+sequence,
  // only swapping the channel/type prefix.
  const rebuildContentIdForEdit = (channelCode, typeCode, existingContentId) => {
    // existingContentId format: CHAN-TYPE-DDMMYY-SEQ  (e.g. YET-LV-090526-003)
    const parts = existingContentId ? existingContentId.split('-') : [];
    // date is 3rd segment, sequence is 4th segment (may have more dashes in future, take last two)
    if (parts.length >= 4) {
      const dateCode = parts[parts.length - 2];
      const seqCode  = parts[parts.length - 1];
      return `${channelCode}-${typeCode}-${dateCode}-${seqCode}`;
    }
    return existingContentId; // fallback: leave unchanged
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Auto-populate channelId and regenerate contentId when channel changes
    if (name === 'channel' && dropdowns.channelIds) {
      const channelId = dropdowns.channelIds[value] || '';
      const channelCode = dropdowns.channelIds?.[value] || 'XXX';
      const typeCode = dropdowns.contentTypeIds?.[formData.contentType] || 'XX';
      const newContentId = isEdit
        ? rebuildContentIdForEdit(channelCode, typeCode, formData.contentId)
        : await generateContentId(value, formData.contentType, dropdowns.channelIds, dropdowns.contentTypeIds);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        channelId,
        contentId: newContentId
      }));
    }
    // Auto-populate contentTypeId and regenerate contentId when contentType changes
    else if (name === 'contentType' && dropdowns.contentTypeIds) {
      const contentTypeId = dropdowns.contentTypeIds[value] || '';
      const channelCode = dropdowns.channelIds?.[formData.channel] || 'XXX';
      const typeCode = dropdowns.contentTypeIds?.[value] || 'XX';
      const newContentId = isEdit
        ? rebuildContentIdForEdit(channelCode, typeCode, formData.contentId)
        : await generateContentId(formData.channel, value, dropdowns.channelIds, dropdowns.contentTypeIds);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        contentTypeId,
        contentId: newContentId
      }));
    }
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle user selection (director, editor, etc.) - now stores username
  const handleUserSelect = (field, idField) => (e) => {
    const selectedUsername = e.target.value;
    
    // Find the user object to get the display name
    let dropdownKey = '';
    if (field === 'director') dropdownKey = 'director';
    else if (field === 'editor') dropdownKey = 'editor';
    else if (field === 'cinematographer') dropdownKey = 'cinematographer';
    else if (field === 'writer') dropdownKey = 'writer';
    
    const users = dropdowns[dropdownKey] || [];
    const selectedUser = users.find(u => u.username === selectedUsername);
    
    setFormData(prev => ({
      ...prev,
      [idField]: selectedUsername, // Store username (e.g., JBDERakshan)
      [field]: selectedUser ? selectedUser.firstName : '' // Store display name for backward compatibility
    }));
  };

  // Handle multi-select for casting
  const handleMultiSelect = (e) => {
    const { value } = e.target;
    const selectedUsernames = typeof value === 'string' ? value.split(',') : value;
    
    // Get actor list
    const actors = dropdowns.actor || [];
    
    // Create castingList (names) and castingIds (usernames)
    const castingNames = selectedUsernames.map(username => {
      const actor = actors.find(a => a.username === username);
      return actor ? actor.firstName : username;
    });
    
    setFormData(prev => ({
      ...prev,
      castingIds: selectedUsernames, // Array of usernames
      castingList: castingNames // Array of names (backward compatibility)
    }));
  };

  // Check for duplicate content
  const checkForDuplicates = async () => {
    if (!formData.contentTitle || !formData.channel || !formData.contentType) {
      return { hasDuplicates: false };
    }

    try {
      const params = new URLSearchParams({
        contentTitle: formData.contentTitle,
        channel: formData.channel,
        contentType: formData.contentType
      });
      
      // Exclude current content when editing
      if (isEdit && id) {
        params.append('excludeId', id);
      }

      const response = await axios.get(`${API_URL}/content/check-duplicate?${params}`);
      return response.data.data;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false };
    }
  };

  const handleScriptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScriptUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('script', file);
      const res = await contentService.uploadScript(id, uploadData);
      setScriptFile(res.data.data.scriptFile);
      setSnackbar({ open: true, message: 'Script uploaded successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to upload script', severity: 'error' });
    } finally {
      setScriptUploading(false);
      e.target.value = '';
    }
  };

  const handleScriptDelete = async () => {
    if (!window.confirm('Remove the script file?')) return;
    setScriptDeleting(true);
    try {
      await contentService.deleteScript(id);
      setScriptFile(null);
      setSnackbar({ open: true, message: 'Script file removed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to remove script', severity: 'error' });
    } finally {
      setScriptDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Status transition validation (edit only)
    const STATUS_ORDER = ['Content Ready', 'Content Approved', 'Planning Completed', 'Shoot Completed', 'Editing Completed', 'Released'];
    const REVISION_STATUS = 'Sent for Revision';
    if (isEdit && originalStatusRef.current && formData.status !== originalStatusRef.current) {
      const oldStatus = originalStatusRef.current;
      const newStatus = formData.status;
      let transitionError = null;

      // Released is terminal — no changes allowed
      if (oldStatus === 'Released') {
        transitionError = 'Cannot change status once content is "Released".';
      }
      // Rules for moving TO "Sent for Revision"
      else if (newStatus === REVISION_STATUS) {
        if (oldStatus !== 'Content Approved' && oldStatus !== 'Planning Completed') {
          transitionError = `"Sent for Revision" can only be set from "Content Approved" or "Planning Completed". Current status is "${oldStatus}".`;
        }
      }
      // Rules for moving FROM "Sent for Revision"
      else if (oldStatus === REVISION_STATUS) {
        if (newStatus !== 'Content Approved' && newStatus !== 'Content Ready') {
          transitionError = `From "Sent for Revision" you can only move to "Content Ready" or "Content Approved".`;
        }
      }
      // Normal linear transitions
      else {
        const oldIdx = STATUS_ORDER.indexOf(oldStatus);
        const newIdx = STATUS_ORDER.indexOf(newStatus);
        if (oldIdx !== -1 && newIdx !== -1) {
          // No skipping forward
          if (newIdx > oldIdx + 1) {
            const required = STATUS_ORDER.slice(oldIdx + 1, newIdx).join(' → ');
            transitionError = `Cannot skip from "${oldStatus}" to "${newStatus}". Must go through: ${required}`;
          }
          // Backward only allowed up to Planning Completed (index 2)
          // i.e. oldIdx must be ≤ 2 to allow going back
          else if (newIdx < oldIdx && oldIdx > 2) {
            transitionError = `Cannot go back from "${oldStatus}". Status changes are locked once Shoot is completed.`;
          }
        }
      }

      if (transitionError) {
        setValidationErrors([{ field: 'Status', message: transitionError }]);
        setErrorDialogOpen(true);
        setSaving(false);
        return;
      }
    }

    // Mandatory validation based on status
    const statusValidationErrors = [];

    // Always required fields
    if (!formData.genre) {
      statusValidationErrors.push({ field: 'Genre', message: 'Genre is required.' });
    }
    if (!formData.directorId) {
      statusValidationErrors.push({ field: 'Director', message: 'Director is required.' });
    }
    if (!formData.shootDate) {
      statusValidationErrors.push({ field: 'Shoot Date', message: 'Shoot Date is required (enter a tentative date if not confirmed).' });
    }

    if (formData.status === 'Planning Completed') {
      if (!formData.castingIds || formData.castingIds.length === 0) {
        statusValidationErrors.push({ field: 'Casting List', message: 'Required when status is "Planning Completed"' });
      }
      if (!formData.cinematographerId) {
        statusValidationErrors.push({ field: 'Cinematographer', message: 'Required when status is "Planning Completed"' });
      }
      if (!formData.shootDate) {
        statusValidationErrors.push({ field: 'Shoot Date', message: 'Required when status is "Planning Completed"' });
      }
      // Long Videos: verify confirmed shoot plan + callsheet exists
      if (formData.contentType === 'Long Video' && isEdit && id) {
        try {
          const planRes = await api.get(`/shoot-plans/content/${id}`);
          const plan = planRes.data?.data?.plan;
          if (!plan || plan.status !== 'confirmed' || !plan.confirmedOptionId) {
            statusValidationErrors.push({ field: 'Shoot Plan', message: 'A confirmed shoot plan is required in Shoot Planner before marking Planning Completed for Long Videos' });
          } else {
            const confirmedOpt = plan.options?.find(o => o.id === plan.confirmedOptionId);
            const scenes = confirmedOpt?.callsheetScenes || [];
            if (!scenes.length) {
              statusValidationErrors.push({ field: 'Callsheet', message: 'Callsheet must have scenes added in Shoot Planner before marking Planning Completed for Long Videos' });
            }
          }
        } catch (_) {
          statusValidationErrors.push({ field: 'Shoot Plan', message: 'Could not verify shoot plan. Please check Shoot Planner before proceeding.' });
        }
      }
    }

    if (formData.status === 'Shoot Completed') {
      if (!formData.castingIds || formData.castingIds.length === 0) {
        statusValidationErrors.push({ field: 'Casting List', message: 'Required when status is "Shoot Completed"' });
      }
      if (!formData.cinematographerId) {
        statusValidationErrors.push({ field: 'Cinematographer', message: 'Required when status is "Shoot Completed"' });
      }
      if (!formData.shootDate) {
        statusValidationErrors.push({ field: 'Shoot Date', message: 'Required when status is "Shoot Completed"' });
      }
      const _dirEffortMin = formData.contentType === 'Long Video' ? 3 : 1;
      if ((formData.directorEfforts || []).length < _dirEffortMin) {
        statusValidationErrors.push({ field: "Director's Effort", message: `At least ${_dirEffortMin} Director's Effort ${_dirEffortMin === 1 ? 'entry' : 'entries'} required for ${formData.contentType || 'this content type'} when marking Shoot Completed` });
      }
    }
    
    if (formData.status === 'Editing Completed') {
      if (!formData.castingIds || formData.castingIds.length === 0) {
        statusValidationErrors.push({ field: 'Casting List', message: 'Required when status is "Editing Completed"' });
      }
      if (!formData.cinematographerId) {
        statusValidationErrors.push({ field: 'Cinematographer', message: 'Required when status is "Editing Completed"' });
      }
      if (!formData.editorId) {
        statusValidationErrors.push({ field: 'Editor', message: 'Required when status is "Editing Completed"' });
      }
      if (!formData.shootDate) {
        statusValidationErrors.push({ field: 'Shoot Date', message: 'Required when status is "Editing Completed"' });
      }
      const _effortMinEC = formData.contentType === 'Long Video' ? 3 : 1;
      if ((formData.editorEfforts || []).length < _effortMinEC) {
        statusValidationErrors.push({ field: "Editor's Effort", message: `At least ${_effortMinEC} Editor's Effort ${_effortMinEC === 1 ? 'entry' : 'entries'} required for ${formData.contentType || 'this content type'} when marking Editing Completed` });
      }
    }

    if (formData.status === 'Released') {
      const ytdValues = ['Yet to Decide', 'Yet To Decide', 'yet to decide', 'YET TO DECIDE'];
      if (!formData.channel || ytdValues.some(v => formData.channel.trim().toLowerCase() === v.toLowerCase())) {
        statusValidationErrors.push({ field: 'Channel', message: 'Channel cannot be "Yet to Decide" when releasing. Please assign a specific channel before marking as Released.' });
      }
      if (formData.contentId && /^(YET|XXX)-/i.test(formData.contentId.trim())) {
        statusValidationErrors.push({ field: 'Content ID', message: 'Content ID still has a placeholder prefix (YET/XXX). Please assign a proper channel before releasing.' });
      }
      if (!formData.castingIds || formData.castingIds.length === 0) {
        statusValidationErrors.push({ field: 'Casting List', message: 'Required when status is "Released"' });
      }
      if (!formData.cinematographerId) {
        statusValidationErrors.push({ field: 'Cinematographer', message: 'Required when status is "Released"' });
      }
      if (!formData.editorId) {
        statusValidationErrors.push({ field: 'Editor', message: 'Required when status is "Released"' });
      }
      if (!formData.shootDate) {
        statusValidationErrors.push({ field: 'Shoot Date', message: 'Required when status is "Released"' });
      }
      if (!formData.releaseDate) {
        statusValidationErrors.push({ field: 'Release Date', message: 'Required when status is "Released"' });
      }
      if (!formData.ytVideoUrl || !formData.ytVideoUrl.trim()) {
        statusValidationErrors.push({ field: 'YouTube Video URL', message: 'Required when status is "Released"' });
      }
      const _effortMinRL = formData.contentType === 'Long Video' ? 3 : 1;
      if ((formData.editorEfforts || []).length < _effortMinRL) {
        statusValidationErrors.push({ field: "Editor's Effort", message: `At least ${_effortMinRL} Editor's Effort ${_effortMinRL === 1 ? 'entry' : 'entries'} required for ${formData.contentType || 'this content type'} when marking Released` });
      }
    }
    
    // Show validation errors if any
    if (statusValidationErrors.length > 0) {
      setSaving(false);
      setValidationErrors(statusValidationErrors);
      setErrorDialogOpen(true);
      return;
    }
    
    // If not proceeding with duplicate, check for duplicates first
    if (!proceedWithDuplicate && !isEdit) {
      try {
        const duplicateCheck = await checkForDuplicates();
        
        if (duplicateCheck.hasDuplicates) {
          setSaving(false);
          setDuplicateData(duplicateCheck);
          setDuplicateDialogOpen(true);
          return; // Stop submission and show warning
        }
      } catch (error) {
        console.error('Duplicate check failed:', error);
        // Continue with submission if check fails
      }
    }

    // Reset proceed flag for next submission
    setProceedWithDuplicate(false);

    try {
      // Prepare data with both old and new formats
      const dataToSend = {
        ...formData,
        // Ensure ID fields are set
        directorId: formData.directorId || '',
        editorId: formData.editorId || '',
        cinematographerId: formData.cinematographerId || '',
        writerId: formData.writerId || '',
        castingIds: formData.castingIds || [],
        castingList: formData.castingList || [],
        editorEfforts: formData.editorEfforts || [],
        directorEfforts: formData.directorEfforts || [],
        // Clean up date fields - convert "Invalid date" or empty strings to null
        shootDate: (formData.shootDate && formData.shootDate !== 'Invalid date' && formData.shootDate !== '') 
          ? formData.shootDate 
          : null,
        releaseDate: (formData.releaseDate && formData.releaseDate !== 'Invalid date' && formData.releaseDate !== '') 
          ? formData.releaseDate 
          : null
      };

      console.log('Submitting content data:', dataToSend);

      // Don't send contentId on update unless it actually changed — avoids unique constraint errors
      if (isEdit) {
        if (!dataToSend.contentId || dataToSend.contentId === originalContentIdRef.current) {
          delete dataToSend.contentId;
        }
      }

      if (isEdit) {
        await contentService.update(id, dataToSend);
        setSnackbar({ open: true, message: 'Content updated successfully!', severity: 'success' });
        await fetchContent();
      } else {
        const response = await contentService.create(dataToSend);
        const newId = response.data?.data?.content?.id;
        // If a script file was selected before saving, upload it now
        if (newId && pendingScriptFile) {
          try {
            const fd = new FormData();
            fd.append('script', pendingScriptFile);
            await contentService.uploadScript(newId, fd);
          } catch (_) { /* non-fatal — content was created */ }
        }
        setSnackbar({ open: true, message: 'Content created successfully!', severity: 'success' });
        setTimeout(() => navigate('/content'), 1500);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if there are validation errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setValidationErrors(error.response.data.errors);
        setErrorDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Error saving content',
          severity: 'error'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={3}>
        {isEdit ? 'Edit Content' : 'Add New Content'}
      </Typography>

      {isEdit && formData.status && (
        <ContentRoadmap content={formData} />
      )}

      {isEdit && id && (
        <Accordion expanded={historyExpanded} onChange={handleHistoryExpand} sx={{ mb: 2, borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon fontSize="small" color="action" />
              <Typography fontWeight={600} variant="body2">Change History</Typography>
              {historyLogs.length > 0 && (
                <Chip label={historyLogs.length} size="small" variant="outlined" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {historyLoading ? (
              <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
            ) : historyLogs.length === 0 ? (
              <Box p={2}><Typography variant="body2" color="text.secondary">No history yet.</Typography></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyLogs.map(log => {
                      const ACTION_COLOR = { CREATE: 'success', STATUS_CHANGE: 'primary', UPDATE: 'default', DELETE: 'error' };
                      return (
                        <TableRow key={log.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                            {new Date(log.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </TableCell>
                          <TableCell>
                            <Chip label={log.action} size="small" color={ACTION_COLOR[log.action] || 'default'} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{log.changedByName || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{log.changedByRole || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {log.action === 'STATUS_CHANGE'
                              ? <span><b>{log.oldStatus}</b> → <b>{log.newStatus}</b></span>
                              : log.changedFields?.length
                                ? log.changedFields.join(', ')
                                : '—'
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Content ID (Auto-generated, updates when channel/type changes) */}
              {formData.contentId && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Content ID"
                    value={formData.contentId}
                    InputProps={{ readOnly: true }}
                    helperText="Auto-generated: {Channel}-{Type}-{DDMMYY}-{Sequential#} (updates when channel or type changes)"
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontWeight: 'bold', 
                        color: 'primary.main' 
                      } 
                    }}
                  />
                </Grid>
              )}

              {/* Content Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Content Title"
                  name="contentTitle"
                  value={formData.contentTitle}
                  onChange={handleChange}
                />
              </Grid>

              {/* Channel */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    name="channel"
                    value={formData.channel}
                    onChange={handleChange}
                    label="Channel"
                  >
                    {(dropdowns.channels || []).map((channel) => (
                      <MenuItem key={channel} value={channel}>{channel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Content Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    name="contentType"
                    value={formData.contentType}
                    onChange={handleChange}
                    label="Content Type"
                  >
                    {(dropdowns.contentTypes || []).map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Genre */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Genre</InputLabel>
                  <Select
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    label="Genre"
                  >
                    {(dropdowns.genres || []).map((g) => (
                      <MenuItem key={g} value={g}>{g}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Casting List (Multi-select with usernames) */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Casting List</InputLabel>
                  <Select
                    multiple
                    name="castingIds"
                    value={formData.castingIds}
                    onChange={handleMultiSelect}
                    input={<OutlinedInput label="Casting List" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((username) => {
                          const actor = (dropdowns.actor || []).find(a => a.username === username);
                          return (
                            <Chip 
                              key={username} 
                              label={actor ? actor.name : username} 
                              size="small" 
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {(dropdowns.actor || []).map((actor) => (
                      <MenuItem key={actor.username} value={actor.username}>
                        {actor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formData.castingIds.length > 0 && (
                  <Button 
                    size="small" 
                    color="error" 
                    onClick={() => setFormData(prev => ({ ...prev, castingIds: [], castingList: [] }))}
                    sx={{ mt: 1 }}
                  >
                    Clear All Casting
                  </Button>
                )}
              </Grid>

              {/* Location Details */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Location Details"
                  name="locationDetails"
                  value={formData.locationDetails}
                  onChange={handleChange}
                />
              </Grid>

              {/* Shoot Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Shoot Date"
                  name="shootDate"
                  value={formData.shootDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Release Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Release Date"
                  name="releaseDate"
                  value={formData.releaseDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Director */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Director</InputLabel>
                  <Select
                    name="directorId"
                    value={formData.directorId}
                    onChange={handleUserSelect('director', 'directorId')}
                    label="Director"
                  >
                    <MenuItem value="">None</MenuItem>
                    {(dropdowns.director || []).map((director) => (
                      <MenuItem key={director.username} value={director.username}>
                        {director.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Editor */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Editor</InputLabel>
                  <Select
                    name="editorId"
                    value={formData.editorId}
                    onChange={handleUserSelect('editor', 'editorId')}
                    label="Editor"
                  >
                    <MenuItem value="">None</MenuItem>
                    {(dropdowns.editor || []).map((editor) => (
                      <MenuItem key={editor.username} value={editor.username}>
                        {editor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Cinematographer */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Cinematographer</InputLabel>
                  <Select
                    name="cinematographerId"
                    value={formData.cinematographerId}
                    onChange={handleUserSelect('cinematographer', 'cinematographerId')}
                    label="Cinematographer"
                  >
                    <MenuItem value="">None</MenuItem>
                    {(dropdowns.cinematographer || []).map((cinematographer) => (
                      <MenuItem key={cinematographer.username} value={cinematographer.username}>
                        {cinematographer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Writer */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Writer</InputLabel>
                  <Select
                    name="writerId"
                    value={formData.writerId}
                    onChange={handleUserSelect('writer', 'writerId')}
                    label="Writer"
                  >
                    <MenuItem value="">None</MenuItem>
                    {(dropdowns.writer || []).map((writer) => (
                      <MenuItem key={writer.username} value={writer.username}>
                        {writer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Status"
                    disabled={!isEdit}
                  >
                    {(dropdowns.statuses || []).filter(s => {
                      if (!isEdit) return s === 'Content Ready'; // new content — locked to Content Ready
                      const STATUS_ORDER = ['Content Ready', 'Content Approved', 'Planning Completed', 'Shoot Completed', 'Editing Completed', 'Released'];
                      const curIdx = STATUS_ORDER.indexOf(originalStatusRef.current);
                      const sIdx = STATUS_ORDER.indexOf(s);
                      if (curIdx === -1) return true;
                      return sIdx === curIdx || sIdx === curIdx + 1;
                    }).map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Video Performance */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Video Performance</InputLabel>
                  <Select
                    name="videoPerformance"
                    value={formData.videoPerformance}
                    onChange={handleChange}
                    label="Video Performance"
                  >
                    <MenuItem value="">Not Rated</MenuItem>
                    {(dropdowns.videoPerformances || []).map((perf) => (
                      <MenuItem key={perf} value={perf}>{perf}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Repository Upload Status — hidden from form, managed separately */}

              {/* YouTube Video URL and Facebook URL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="YouTube Video URL"
                  name="ytVideoUrl"
                  value={formData.ytVideoUrl || ''}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  helperText={formData.status === 'Released' ? '* Required for Released status' : 'Optional'}
                  error={formData.status === 'Released' && !formData.ytVideoUrl?.trim()}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Facebook Video URL"
                  name="facebookUrl"
                  value={formData.facebookUrl || ''}
                  onChange={handleChange}
                  placeholder="https://www.facebook.com/..."
                  helperText="Optional"
                />
              </Grid>

              {/* Integrated Ad — Long Video only */}
              {formData.contentType === 'Long Video' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData.integratedAdIncluded)}
                        onChange={(e) => setFormData(prev => ({ ...prev, integratedAdIncluded: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Integrated Ad Included</Typography>
                        <Typography variant="caption" color="text.secondary">Check if this Long Video contains an integrated advertisement</Typography>
                      </Box>
                    }
                  />
                </Grid>
              )}

              {/* Director's Effort */}
              <Grid item xs={12}>
                <Box sx={{ border: '2px solid', borderColor: '#0f766e', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', letterSpacing: 0.5 }}>🎬 Director's Effort</Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: '#f0fdfa' }}>
                  <Grid container spacing={1} alignItems="center" mb={1.5}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Director</InputLabel>
                        <Select
                          value={newDirectorEffortRow.directorId}
                          label="Director"
                          onChange={(e) => {
                            const dr = (dropdowns.director || []).find(d => d.username === e.target.value);
                            setNewDirectorEffortRow(r => ({ ...r, directorId: e.target.value, directorName: dr ? dr.firstName + (dr.lastName ? ' ' + dr.lastName : '') : e.target.value }));
                          }}
                        >
                          {(dropdowns.director || []).map(dr => (
                            <MenuItem key={dr.username} value={dr.username}>{dr.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Work Type</InputLabel>
                        <Select
                          value={newDirectorEffortRow.workType}
                          label="Work Type"
                          onChange={(e) => setNewDirectorEffortRow(r => ({ ...r, workType: e.target.value }))}
                        >
                          {(dropdowns.director_work_types || []).map(w => (
                            <MenuItem key={w} value={w}>{w}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={8} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Effort (hours)"
                        value={newDirectorEffortRow.effort}
                        onChange={(e) => setNewDirectorEffortRow(r => ({ ...r, effort: e.target.value }))}
                        placeholder="e.g. 2.5"
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d6659' } }}
                        disabled={!newDirectorEffortRow.directorId || !newDirectorEffortRow.workType || !newDirectorEffortRow.effort}
                        onClick={() => {
                          const isDuplicate = (formData.directorEfforts || []).some(r => r.directorId === newDirectorEffortRow.directorId && r.workType === newDirectorEffortRow.workType);
                          if (isDuplicate) { alert(`${newDirectorEffortRow.directorName || newDirectorEffortRow.directorId} already has a "${newDirectorEffortRow.workType}" entry.`); return; }
                          setFormData(prev => ({ ...prev, directorEfforts: [...(prev.directorEfforts || []), { ...newDirectorEffortRow }] }));
                          setNewDirectorEffortRow({ directorId: '', directorName: '', workType: '', effort: '' });
                        }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                  {(formData.directorEfforts || []).length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#99f6e4' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#ccfbf1' }}>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Director</TableCell>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Work Type</TableCell>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Effort (hrs)</TableCell>
                            <TableCell width={48}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(formData.directorEfforts || []).map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f0fdfa' } }}>
                              <TableCell>{row.directorName || row.directorId}</TableCell>
                              <TableCell><Chip label={row.workType} size="small" sx={{ bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 600 }} /></TableCell>
                              <TableCell><strong>{row.effort}</strong> hrs</TableCell>
                              <TableCell>
                                <IconButton size="small" color="error"
                                  onClick={() => setFormData(prev => ({ ...prev, directorEfforts: prev.directorEfforts.filter((_, i) => i !== idx) }))}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  </Box>
                </Box>
              </Grid>

              {/* Editor's Effort */}
              <Grid item xs={12}>
                <Box sx={{ border: '2px solid', borderColor: '#7c3aed', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', letterSpacing: 0.5 }}>✂️ Editor's Effort</Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: '#faf5ff' }}>
                  {/* Add row form */}
                  <Grid container spacing={1} alignItems="center" mb={1.5}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Editor</InputLabel>
                        <Select
                          value={newEffortRow.editorId}
                          label="Editor"
                          onChange={(e) => {
                            const ed = (dropdowns.editor || []).find(d => d.username === e.target.value);
                            setNewEffortRow(r => ({ ...r, editorId: e.target.value, editorName: ed ? ed.firstName + (ed.lastName ? ' ' + ed.lastName : '') : e.target.value }));
                          }}
                        >
                          {(dropdowns.editor || []).map(ed => (
                            <MenuItem key={ed.username} value={ed.username}>{ed.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Work Type</InputLabel>
                        <Select
                          value={newEffortRow.workType}
                          label="Work Type"
                          onChange={(e) => setNewEffortRow(r => ({ ...r, workType: e.target.value }))}
                        >
                          {(dropdowns.editor_work_types || []).map(w => (
                            <MenuItem key={w} value={w}>{w}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={8} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Effort (hours)"
                        value={newEffortRow.effort}
                        onChange={(e) => setNewEffortRow(r => ({ ...r, effort: e.target.value }))}
                        placeholder="e.g. 2.5"
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
                        disabled={!newEffortRow.editorId || !newEffortRow.workType || !newEffortRow.effort}
                        onClick={() => {
                          const isDuplicate = (formData.editorEfforts || []).some(r => r.editorId === newEffortRow.editorId && r.workType === newEffortRow.workType);
                          if (isDuplicate) { alert(`${newEffortRow.editorName || newEffortRow.editorId} already has a "${newEffortRow.workType}" entry.`); return; }
                          setFormData(prev => ({ ...prev, editorEfforts: [...(prev.editorEfforts || []), { ...newEffortRow }] }));
                          setNewEffortRow({ editorId: '', editorName: '', workType: '', effort: '' });
                        }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Effort entries table */}
                  {(formData.editorEfforts || []).length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#ddd6fe' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#ede9fe' }}>
                            <TableCell sx={{ color: '#5b21b6', fontWeight: 700 }}>Editor</TableCell>
                            <TableCell sx={{ color: '#5b21b6', fontWeight: 700 }}>Work Type</TableCell>
                            <TableCell sx={{ color: '#5b21b6', fontWeight: 700 }}>Effort (hrs)</TableCell>
                            <TableCell width={48}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(formData.editorEfforts || []).map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f5f3ff' } }}>
                              <TableCell>{row.editorName || row.editorId}</TableCell>
                              <TableCell><Chip label={row.workType} size="small" sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 600 }} /></TableCell>
                              <TableCell><strong>{row.effort}</strong> hrs</TableCell>
                              <TableCell>
                                <IconButton size="small" color="error"
                                  onClick={() => setFormData(prev => ({ ...prev, editorEfforts: prev.editorEfforts.filter((_, i) => i !== idx) }))}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  </Box>
                </Box>
              </Grid>

              {/* Remarks */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </Grid>

              {/* Script / Content File */}
              {canUploadScript && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5} display="flex" alignItems="center" gap={1}>
                      <FileIcon fontSize="small" color="primary" /> Script / Content File
                    </Typography>

                    {/* Edit mode — file already uploaded */}
                    {isEdit && scriptFile ? (
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Chip
                          icon={<FileIcon />}
                          label={scriptFile.originalName}
                          color="primary"
                          variant="outlined"
                          onClick={() => openScriptSecure(id)}
                          sx={{ cursor: 'pointer', maxWidth: 300 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {(scriptFile.size / 1024).toFixed(1)} KB · Uploaded by {scriptFile.uploadedBy} · {new Date(scriptFile.uploadedAt).toLocaleDateString('en-IN')}
                        </Typography>
                        <Button size="small" variant="outlined" component="label"
                          startIcon={<UploadFileIcon />} disabled={scriptUploading}>
                          Replace
                          <input type="file" hidden accept=".pdf,.doc,.docx,.txt" onChange={handleScriptUpload} />
                        </Button>
                        <Button size="small" variant="outlined" color="error"
                          startIcon={scriptDeleting ? <CircularProgress size={14} /> : <DeleteIcon />}
                          disabled={scriptDeleting} onClick={handleScriptDelete}>
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        {/* New content — show pending file name or picker */}
                        {!isEdit && pendingScriptFile ? (
                          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Chip
                              icon={<FileIcon />}
                              label={pendingScriptFile.name}
                              color="primary"
                              variant="outlined"
                              sx={{ maxWidth: 300 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {(pendingScriptFile.size / 1024).toFixed(1)} KB · Will upload on Create
                            </Typography>
                            <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                              Change
                              <input type="file" hidden accept=".pdf,.doc,.docx,.txt"
                                onChange={e => { if (e.target.files[0]) setPendingScriptFile(e.target.files[0]); e.target.value = ''; }} />
                            </Button>
                            <Button size="small" variant="outlined" color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => setPendingScriptFile(null)}>
                              Remove
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={scriptUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                            disabled={scriptUploading}
                          >
                            {scriptUploading ? 'Uploading…' : 'Upload Script / Content File'}
                            <input type="file" hidden accept=".pdf,.doc,.docx,.txt"
                              onChange={isEdit ? handleScriptUpload : (e => { if (e.target.files[0]) setPendingScriptFile(e.target.files[0]); e.target.value = ''; })} />
                          </Button>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          Accepted: PDF, Word (.doc/.docx), TXT · Max 5 MB
                          {!isEdit && ' · File will be attached after content is created'}
                        </Typography>
                        {scriptUploading && <LinearProgress sx={{ mt: 1 }} />}
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Employees can only view the attached script */}
              {!canUploadScript && isEdit && scriptFile && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5} display="flex" alignItems="center" gap={1}>
                      <FileIcon fontSize="small" color="primary" /> Script / Content File
                    </Typography>
                    <Chip
                      icon={<FileIcon />}
                      label={scriptFile.originalName}
                      color="primary"
                      variant="outlined"
                      onClick={() => openScriptSecure(id)}
                      sx={{ cursor: 'pointer', maxWidth: 300 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {(scriptFile.size / 1024).toFixed(1)} KB · Click to view/download
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => navigate('/content')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Validation Errors Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{ sx: { mx: 2, borderRadius: 2, border: '2px solid #d32f2f' } }}
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          ⚠️ Please fix the following before saving
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <List disablePadding>
            {validationErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0.75, px: 0, borderBottom: index < validationErrors.length - 1 ? '1px solid #ffebee' : 'none' }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, minWidth: 8 }}>✗</Typography>
                      <Box>
                        <Typography variant="body2" fontWeight={700} color="error.main">{error.field}</Typography>
                        <Typography variant="caption" color="text.secondary">{error.message}</Typography>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            color="error"
            fullWidth
            size="large"
          >
            OK, Fix These
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog 
        open={duplicateDialogOpen} 
        onClose={() => {
          setDuplicateDialogOpen(false);
          setDuplicateData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          ⚠️ Potential Duplicate Content
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Found {duplicateData?.count} existing content with the same Title, Channel, and Content Type:
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Your Content:</strong>
          </Typography>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="body2">
              • <strong>Title:</strong> {formData.contentTitle}
            </Typography>
            <Typography variant="body2">
              • <strong>Channel:</strong> {formData.channel}
            </Typography>
            <Typography variant="body2">
              • <strong>Type:</strong> {formData.contentType}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Existing Content:</strong>
          </Typography>
          <List>
            {duplicateData?.duplicates?.map((dup, index) => (
              <ListItem 
                key={index} 
                sx={{ 
                  py: 1, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  mb: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {dup.contentTitle}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        Content ID: {dup.contentId} | Status: {dup.status}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Created: {new Date(dup.createdAt).toLocaleDateString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              You can still proceed if this is intentional (e.g., remake or new version)
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDuplicateDialogOpen(false);
              setDuplicateData(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={(e) => {
              setDuplicateDialogOpen(false);
              setProceedWithDuplicate(true);
              // Trigger submission again with proceed flag set
              setTimeout(() => {
                const form = e.target.closest('form') || document.querySelector('form');
                if (form) {
                  form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
              }, 100);
            }}
            variant="contained"
            color="warning"
            autoFocus
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContentForm;
